// Split shipping on a PO (Stephanie 2026-10-16): this item ships to more than
// one address — each address with how many of each size go there. Shown on the
// staff PO page and the supplier's PO page, under the item's sizes.
import { MapPin } from "lucide-react";

export interface PoSplit {
  address: Record<string, unknown>;
  quantities: Array<{ color: string | null; size: string | null; qty: number }>;
  quantity: number;
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);

/** Pure: the address as lines (company / attn / street / city). */
export function splitAddressLines(a: Record<string, unknown>): string[] {
  const cityLine = [str(a.city), [str(a.stateCode), str(a.postalCode)].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [
    str(a.companyName) ?? str(a.label),
    str(a.attention) ? `Attn: ${str(a.attention)}` : null,
    [str(a.street1), str(a.street2)].filter(Boolean).join(", ") || null,
    cityLine || null,
  ].filter((x): x is string => Boolean(x));
}

/** Pure: "Black L 12 · Black M 12". */
export function splitQtyText(q: PoSplit["quantities"]): string {
  return q.map((x) => `${[x.color, x.size].filter(Boolean).join(" ") || "Qty"} ${x.qty}`).join(" · ");
}

export function SplitShipTo({ splits }: { splits: PoSplit[] | undefined }) {
  if (!splits || splits.length === 0) return null;
  return (
    <div className="mt-4 rounded-lg border border-rust-200 bg-cream-50 p-4">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-rust-600">
        <MapPin className="h-3.5 w-3.5" /> Split shipment — ships to {splits.length} addresses
      </p>
      <ol className="mt-3 space-y-3">
        {splits.map((s, i) => (
          <li key={i} className="grid gap-1 text-sm sm:grid-cols-[1fr_auto] sm:gap-4">
            <div className="text-brown-500">
              {splitAddressLines(s.address).map((l, j) => (
                <p key={j} className={j === 0 ? "font-semibold" : undefined}>{l}</p>
              ))}
            </div>
            <div className="text-right">
              <p className="font-semibold tabular-nums text-brown-500">{s.quantity.toLocaleString()} pcs</p>
              {s.quantities.length > 0 && <p className="text-[12px] text-brown-400">{splitQtyText(s.quantities)}</p>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
